// eslint-disable-next-line no-restricted-imports
import {
  CancelledError,
  experimental_streamedQuery,
  type QueryFunction,
} from '@tanstack/react-query';
import type {Observable} from 'protoc-gen-ts';

type StreamTrailers = Record<string, string[]>;

export type UnresolvedStream<Item = unknown> = {
  stream: Observable<Item>;
  trailers: StreamTrailers;
};

export type ResolvedStream<Item = unknown> = {
  items: Item[];
  trailers: StreamTrailers;
};

export const hasUnresolvedStream = (
  data: unknown,
): data is UnresolvedStream => {
  return Boolean(
    data &&
      typeof data === 'object' &&
      'stream' in data &&
      typeof (data as UnresolvedStream).stream?.subscribe === 'function',
  );
};

/**
 * Turns a gRPC server stream into a react-query query function.
 * The query stays pending until the first item arrives, stays in `fetchStatus: 'fetching'`
 * while the stream is open and resolves with all items once the stream ends.
 * Each snapshot replaces the previous one, so a refetch never appends to the items of an earlier stream.
 * A cancelled fetch (refetch, unmount) closes the stream it opened.
 */
export const createStreamedQueryFn = <Item>(
  response: UnresolvedStream<Item>,
): QueryFunction<ResolvedStream<Item>> => {
  const streamedQueryFn = experimental_streamedQuery<
    ResolvedStream<Item>,
    ResolvedStream<Item>
  >({
    streamFn: ({signal}) => readSnapshots(response, signal),
    reducer: (_previousSnapshot, snapshot) => snapshot,
    initialValue: {items: [], trailers: response.trailers},
  });

  return context => {
    // the stream is opened after the fetcher resolved, so a fetch that a newer refetch
    // cancelled in the meantime must not reset the query state of the live stream
    if (context.signal.aborted) {
      throw new CancelledError();
    }

    return streamedQueryFn(context);
  };
};

async function* readSnapshots<Item>(
  response: UnresolvedStream<Item>,
  signal: AbortSignal,
): AsyncGenerator<ResolvedStream<Item>> {
  let items: Item[] = [];

  for await (const item of toAsyncIterable(response.stream, signal)) {
    items = [...items, item];
    // the gRPC console logger prints the live stream object, so the items are attached to it
    (response.stream as Observable<Item> & {items?: Item[]}).items = items;

    if (process.browser) {
      yield {items, trailers: response.trailers};
    }
  }

  // suspense on the server resumes as soon as the cache holds data, so the
  // server gets one snapshot with the complete list once the stream has ended
  if (!process.browser) {
    yield {items, trailers: response.trailers};
  }
}

type Subscription = (() => void) | {unsubscribe: () => void};
type QueueEntry<Item> = IteratorResult<Item> | {error: unknown};

const toAsyncIterable = <Item>(
  observable: Observable<Item>,
  signal: AbortSignal,
): AsyncIterable<Item> => ({
  [Symbol.asyncIterator]() {
    const queue: Array<QueueEntry<Item>> = [];
    let wakeUp: (() => void) | undefined;

    const enqueue = (entry: QueueEntry<Item>) => {
      queue.push(entry);
      wakeUp?.();
      wakeUp = undefined;
    };

    const subscription = observable.subscribe({
      next: item => enqueue({done: false, value: item}),
      error: error => enqueue({error}),
      complete: () => enqueue({done: true, value: undefined}),
    }) as Subscription;

    const stop = () => {
      signal.removeEventListener('abort', onAbort);
      if (typeof subscription === 'function') {
        subscription();
      } else {
        subscription.unsubscribe();
      }
    };

    const onAbort = () => {
      stop();
      enqueue({done: true, value: undefined});
    };

    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener('abort', onAbort, {once: true});
    }

    return {
      async next() {
        let entry = queue.shift();
        while (!entry) {
          await new Promise<void>(resolve => {
            wakeUp = resolve;
          });
          entry = queue.shift();
        }

        if ('error' in entry) {
          stop();
          throw entry.error;
        }
        if (entry.done) {
          stop();
        }

        return entry;
      },
      async return() {
        stop();
        return {done: true, value: undefined};
      },
    };
  },
});
